(function (browser) {
  'use strict';

  function describePack(value, productTitle) {
    const title = String(productTitle || '').toLowerCase();
    const label = String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
    if (/variety|assort|sampler|mixed|bundle|\bkit\b/.test(title + ' ' + label)) return null;

    let match = label.match(/^(\d+|one)\s*(bags?|box(?:es)?|packs?|bottles?|patch(?:es)?)$/);
    if (match) {
      const units = { bag: 'bag', bags: 'bag', box: 'box', boxes: 'box', pack: 'pack', packs: 'pack', bottle: 'bottle', bottles: 'bottle', patch: 'patch', patches: 'patch' };
      const unit = units[match[2]];
      return { count: match[1] === 'one' ? 1 : Number(match[1]), unit };
    }

    match = label.match(/^(\d+)\s*[- ]\s*months? supply$/);
    if (match) return { count: Number(match[1]), unit: 'month-supply' };
    if (/^30\s*[- ]\s*day supply$/.test(label)) return { count: 1, unit: 'month-supply' };

    match = label.match(/^one box\s*[- ]\s*(\d+) cups$/);
    if (match) return { count: 1, unit: 'cup-box-' + match[1] };
    match = label.match(/^(\d+)\s*[- ]\s*pack\s*[- ]\s*(\d+) cups$/);
    if (match && Number(match[2]) % Number(match[1]) === 0) {
      return { count: Number(match[1]), unit: 'cup-box-' + Number(match[2]) / Number(match[1]) };
    }

    if (/^1 steep bag$/.test(label)) return { count: 1, unit: 'steep-bag' };
    if (/^10\s*[- ]\s*pack box$/.test(label) && /steep/.test(title)) return { count: 10, unit: 'steep-bag' };
    return null;
  }

  function matchingSingle(product, selected) {
    const options = selected.options || [];
    const candidates = [];
    options.forEach((value, position) => {
      const pack = describePack(value, product.title);
      if (!pack || pack.count <= 1 || !Number.isSafeInteger(pack.count)) return;
      const singles = (product.variants || []).filter((variant) => {
        if (variant.available === false || !Number.isSafeInteger(variant.price) || variant.price <= 0) return false;
        if ((variant.options || []).some((other, index) => index !== position && other !== options[index])) return false;
        const possible = describePack(variant.options?.[position], product.title);
        return possible?.count === 1 && possible.unit === pack.unit;
      });
      if (singles.length === 1) candidates.push({ count: pack.count, price: singles[0].price });
    });
    return candidates.length === 1 ? candidates[0] : null;
  }

  function calculate(product, variantId, sellingPlanId, quantity) {
    const variant = product?.variants?.find((entry) => String(entry.id) === String(variantId));
    if (!variant || variant.available === false || !Number.isSafeInteger(quantity) || quantity < 1) return null;
    const allocation = variant.selling_plan_allocations?.find(
      (entry) => String(entry.selling_plan_id) === String(sellingPlanId)
    );
    if (!allocation || !Number.isSafeInteger(allocation.price) || allocation.price <= 0) return null;

    const single = matchingSingle(product, variant);
    const individualBaseline = single && single.price * single.count * quantity;
    const packBaseline = variant.price * quantity;
    const paid = allocation.price * quantity;
    const totalBaseline = individualBaseline > paid ? individualBaseline : null;
    const baseline = totalBaseline || packBaseline;
    if (!Number.isSafeInteger(baseline) || baseline <= paid) return null;
    const saved = baseline - paid;
    return {
      kind: totalBaseline ? 'total' : 'subscription',
      baseline,
      paid,
      saved,
      percent: Math.floor(saved * 100 / baseline)
    };
  }

  function variantForOption(product, selected, position, value) {
    const matches = (product?.variants || []).filter((variant) =>
      variant.available !== false &&
      (variant.options || []).every((option, index) =>
        index === position ? option === value : option === selected?.options?.[index]
      )
    );
    return matches.length === 1 ? matches[0] : null;
  }

  function equivalentPlanId(product, variant, selectedPlanId) {
    if (variant?.selling_plan_allocations?.some((entry) => String(entry.selling_plan_id) === String(selectedPlanId))) return selectedPlanId;
    const plans = (product?.selling_plan_groups || []).flatMap((group) => group.selling_plans || []);
    const selectedPlan = plans.find((plan) => String(plan.id) === String(selectedPlanId));
    const comparable = (selectedPlan?.options || []).filter((option) => option.name !== 'Recharge Plan ID');
    if (!comparable.length) return null;
    const candidates = (variant?.selling_plan_allocations || []).filter((allocation) => {
      const plan = plans.find((entry) => String(entry.id) === String(allocation.selling_plan_id));
      const options = (plan?.options || []).filter((option) => option.name !== 'Recharge Plan ID');
      return options.length === comparable.length && comparable.every((option) =>
        options.some((candidate) => candidate.name === option.name && candidate.value === option.value)
      );
    });
    return candidates.length && new Set(candidates.map((entry) => entry.price)).size === 1
      ? candidates[0].selling_plan_id
      : null;
  }

  function extraPercent(variant, planId) {
    const allocation = variant?.selling_plan_allocations?.find((entry) => String(entry.selling_plan_id) === String(planId));
    return Number.isSafeInteger(variant?.price) && Number.isSafeInteger(allocation?.price) && variant.price > allocation.price
      ? Math.floor((variant.price - allocation.price) * 100 / variant.price)
      : null;
  }

  function savingsCopy(result, money) {
    if (!result) return null;
    const total = result.kind === 'total';
    return {
      main: total
        ? `Save ${money(result.saved)} total (${result.percent}%)`
        : `Save ${money(result.saved)} with subscription (${result.percent}%)`,
      badge: total
        ? `Save ${money(result.saved)} total (${result.percent}%)`
        : `Save ${money(result.saved)} extra (${result.percent}%)`,
      baseline: total ? 'vs buying individual packs' : 'vs this pack one-time'
    };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { describePack, matchingSingle, calculate, variantForOption, equivalentPlanId, extraPercent, savingsCopy };
  }
  if (!browser?.document) return;

  const script = document.currentScript;
  if (!script?.dataset.formId || !script.dataset.productId || !script.dataset.productSource) return;
  const form = document.getElementById(script?.dataset.formId);
  const container = document.querySelector('[data-pack-subscription-savings="' + script?.dataset.productId + '"]');
  const source = document.querySelector(script.dataset.productSource);
  if (!form || !container || !source) return;
  if (script.dataset.currency !== script.dataset.shopCurrency) return;

  let product;
  try {
    const data = JSON.parse(source.textContent);
    product = data.product || data;
  } catch (_) {
    return;
  }
  const root = container.closest('.shopify-section') || container.parentElement;
  const currency = script.dataset.currency || 'USD';
  const money = (cents) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  }).format(cents / 100);
  let widget;
  let observedShadow;
  let shadowObserver;
  let observedPlansShadow;
  let plansObserver;
  let observedBenefitsShadow;
  let benefitsObserver;
  let queued = false;
  const originalPlanLabels = new WeakMap();

  function setText(node, value) {
    if (node && node.textContent !== value) node.textContent = value;
  }

  function updateSubscriptionBadge(result) {
    const badge = observedShadow?.querySelector('#rc-subscription-badge');
    if (!badge) return;
    const display = result ? '' : 'none';
    if (badge.style.display !== display) badge.style.display = display;
    if (!result) return;
    const { badge: label, baseline } = savingsCopy(result, money);
    const textNode = badge.lastChild;
    if (textNode?.nodeType === Node.TEXT_NODE && textNode.nodeValue !== label) textNode.nodeValue = label;
    if (badge.title !== baseline) badge.title = baseline;
    if (badge.getAttribute('aria-label') !== `${label} ${baseline}`) badge.setAttribute('aria-label', `${label} ${baseline}`);
  }

  function updatePlanOnlyCopy(variant, sellingPlanId) {
    observedPlansShadow?.querySelectorAll('input[name="rc_plan"]').forEach((input) => {
      const percent = extraPercent(variant, input.value);
      const discount = input.closest('label')?.querySelector('.rc-plans-button__discount');
      const textNode = discount?.lastChild;
      const label = percent === null ? '' : `Extra ${percent}%`;
      if (textNode?.nodeType === Node.TEXT_NODE && textNode.nodeValue !== label) textNode.nodeValue = label;
      if (!originalPlanLabels.has(input)) originalPlanLabels.set(input, input.getAttribute('aria-label') || '');
      const original = originalPlanLabels.get(input);
      const accessible = original.replace(/save\s*\d+(?:\.\d+)?%/i,
        percent === null ? '' : `extra ${percent}% vs this pack one-time`).trim();
      if (input.getAttribute('aria-label') !== accessible) input.setAttribute('aria-label', accessible);
    });
    const strong = Array.from(observedBenefitsShadow?.querySelectorAll('.rc-benefits__list strong') || [])
      .find((node) => /lock in|extra savings/i.test(node.textContent));
    if (strong) {
      const percent = extraPercent(variant, sellingPlanId);
      const item = strong.closest('li');
      if (item && item.style.display !== (percent === null ? 'none' : '')) item.style.display = percent === null ? 'none' : '';
      if (percent !== null) setText(strong, `Lock in an extra ${percent}% off this pack,`);
    }
  }

  function updatePackBadges(selected, sellingPlanId, isSubscription) {
    root.querySelectorAll('.block-swatch').forEach((tile) => {
      const input = tile.querySelector('input.block-swatch__radio[data-option-position]');
      const label = tile.querySelector('label.block-swatch__item');
      if (!input || !label) return;
      const position = Number(input.dataset.optionPosition) - 1;
      const pack = describePack(input.value, product.title);
      const variant = pack?.count > 1 && variantForOption(product, selected, position, input.value);
      const planId = variant && equivalentPlanId(product, variant, sellingPlanId);
      const result = isSubscription && planId && calculate(product, variant.id, planId, 1);
      let badge = label.querySelector('[data-pack-total-savings-badge]');
      if (!result || result.kind !== 'total') {
        label.classList.remove('pack-total-savings-active');
        badge?.remove();
        return;
      }
      label.classList.add('pack-total-savings-active');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'block-swatch__item-text-discount';
        badge.dataset.packTotalSavingsBadge = '';
        label.appendChild(badge);
      }
      setText(badge, `Save ${result.percent}% total`);
      const description = `Save ${money(result.saved)} total per pack vs buying individual packs`;
      if (badge.title !== description) badge.title = description;
    });
  }

  function render() {
    queued = false;
    const current = root.querySelector('recharge-subscription-widget');
    if (current !== widget || current?.shadowRoot !== observedShadow) {
      shadowObserver?.disconnect();
      plansObserver?.disconnect();
      benefitsObserver?.disconnect();
      widget = current;
      observedShadow = widget?.shadowRoot || null;
      observedPlansShadow = null;
      observedBenefitsShadow = null;
      if (observedShadow) {
        observedShadow.addEventListener('change', schedule);
        shadowObserver = new MutationObserver(schedule);
        shadowObserver.observe(observedShadow, { subtree: true, childList: true, characterData: true, attributes: true });
      }
    }

    const plansShadow = observedShadow?.querySelector('rc-selling-plans')?.shadowRoot || null;
    if (plansShadow !== observedPlansShadow) {
      plansObserver?.disconnect();
      observedPlansShadow = plansShadow;
      if (plansShadow) {
        plansShadow.addEventListener('change', schedule);
        plansObserver = new MutationObserver(schedule);
        plansObserver.observe(plansShadow, { subtree: true, childList: true, characterData: true, attributes: true });
      }
    }
    const benefitsShadow = observedShadow?.querySelector('rc-benefits')?.shadowRoot || null;
    if (benefitsShadow !== observedBenefitsShadow) {
      benefitsObserver?.disconnect();
      observedBenefitsShadow = benefitsShadow;
      if (benefitsShadow) {
        benefitsObserver = new MutationObserver(schedule);
        benefitsObserver.observe(benefitsShadow, { subtree: true, childList: true, characterData: true, attributes: true });
      }
    }

    const isSubscription = widget?.shadowRoot?.querySelector('input[name="purchaseOption"]:checked')?.value === 'subscription';
    const variantId = form.querySelector('[name="id"]')?.value;
    const selected = product.variants?.find((entry) => String(entry.id) === String(variantId));
    const sellingPlanId = (isSubscription && form.querySelector('[name="selling_plan"]')?.value) || widget?.selectedVariantPlan?.external_plan_id;
    const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
    const result = calculate(
      product,
      variantId,
      sellingPlanId,
      quantity
    );
    updateSubscriptionBadge(result);
    updatePlanOnlyCopy(selected, sellingPlanId);
    updatePackBadges(selected, sellingPlanId, isSubscription);
    container.hidden = !(isSubscription && result);
    if (!isSubscription || !result) return;
    const { main, baseline } = savingsCopy(result, money);
    const mainNode = container.querySelector('[data-pack-savings-main]');
    const baselineNode = container.querySelector('[data-pack-savings-baseline]');
    setText(mainNode, main);
    setText(baselineNode, baseline);
  }

  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(render);
  }

  const discovery = new MutationObserver(schedule);
  discovery.observe(root, { subtree: true, childList: true });
  form.addEventListener('change', schedule);
  form.addEventListener('input', schedule);
  form.addEventListener('variant:changed', schedule);
  root.addEventListener('click', () => { schedule(); setTimeout(schedule, 150); });
  window.addEventListener('load', schedule, { once: true });
  customElements.whenDefined('recharge-subscription-widget').then(schedule);
  let discoveryChecks = 0;
  const discoveryTimer = window.setInterval(() => {
    schedule();
    discoveryChecks += 1;
    if ((observedShadow && observedPlansShadow && observedBenefitsShadow) || discoveryChecks >= 40) window.clearInterval(discoveryTimer);
  }, 250);
  schedule();
})(typeof window === 'undefined' ? null : window);
